import api from './api';

// ── Connections cache ──────────────────────────────────────────────────────
// The connected-platform lists (`getConnections` / `getProfiles`) are read on
// nearly every page and rarely change, so we cache them in-memory for a short
// TTL to avoid a server round-trip on every page open. The cache is invalidated
// immediately on any mutation (connect/disconnect/primary/rename/sync) and on
// logout, so it never serves stale or cross-user data. Pass force=true to bypass
// it (e.g. a manual "Refresh" button).
const CONNECTIONS_TTL_MS = 60_000; // 60s
const _cache = { connections: null, connectionsAt: 0, profiles: null, profilesAt: 0 };
const _isFresh = (at) => at > 0 && Date.now() - at < CONNECTIONS_TTL_MS;
const invalidateConnections = () => {
  _cache.connections = null;
  _cache.connectionsAt = 0;
  _cache.profiles = null;
  _cache.profilesAt = 0;
};

const platformService = {
  // ── Platform Definitions (public, no auth required) ──────────────────────

  // Fetch all active platform definitions ordered by displayOrder
  getPlatformDefinitions: async () => {
    const response = await api.get('/api/v1/platforms/definitions');
    return response.data.data;
  },

  // Fetch a single platform definition by key (e.g. "YOUTUBE")
  getPlatformDefinition: async (platformKey) => {
    const response = await api.get(`/api/v1/platforms/definitions/${platformKey}`);
    return response.data.data;
  },

  // ── Platform Connections ─────────────────────────────────────────────────

  // Get all connected platforms for the current user (cached; force=true bypasses)
  getConnections: async (force = false) => {
    if (!force && _isFresh(_cache.connectionsAt)) return _cache.connections;
    const response = await api.get('/api/v1/platforms/connections');
    _cache.connections = response.data.data;
    _cache.connectionsAt = Date.now();
    return _cache.connections;
  },

  // Clear the cached connections/profiles — call on logout or after external changes
  invalidateConnections,

  // Get a specific connection by UUID
  getConnection: async (id) => {
    const response = await api.get(`/api/v1/platforms/connections/${id}`);
    return response.data.data;
  },

  // Check token validity for a connection
  getConnectionStatus: async (id) => {
    const response = await api.get(`/api/v1/platforms/connections/${id}/status`);
    return response.data.data;
  },

  // Get channel/profile info for a connection
  getChannelInfo: async (id) => {
    const response = await api.get(`/api/v1/platforms/connections/${id}/channel-info`);
    return response.data.data;
  },

  // ── OAuth ────────────────────────────────────────────────────────────────

  // Get the OAuth2 authorization URL for a platform (e.g. "YOUTUBE")
  getAuthorizationUrl: async (platform) => {
    const response = await api.get('/api/v1/platforms/oauth/authorize', { params: { platform } });
    return response.data.data; // { authorizationUrl, state }
  },

  // Connect a platform using the OAuth authorization code
  connect: async ({ platform, code, redirectUri, state }) => {
    const response = await api.post('/api/v1/platforms/connect', { platform, code, redirectUri, state });
    invalidateConnections();
    return response.data.data;
  },

  // Disconnect a platform connection by UUID
  disconnect: async (connectionId) => {
    await api.delete(`/api/v1/platforms/disconnect/${connectionId}`);
    invalidateConnections();
  },

  // Refresh the OAuth access token for a connection
  refresh: async (connectionId) => {
    const response = await api.post(`/api/v1/platforms/refresh/${connectionId}`);
    invalidateConnections();
    return response.data.data;
  },

  // Set a connection as the primary for its platform
  setPrimary: async (connectionId) => {
    const response = await api.put(`/api/v1/platforms/connections/${connectionId}/primary`);
    invalidateConnections();
    return response.data.data;
  },

  // Update the display name for a connection
  updateDisplayName: async (connectionId, displayName) => {
    const response = await api.patch(`/api/v1/platforms/connections/${connectionId}/display-name`, null, {
      params: { displayName },
    });
    invalidateConnections();
    return response.data.data;
  },

  // Get all connections for a specific platform
  getConnectionsByPlatform: async (platform) => {
    const response = await api.get(`/api/v1/platforms/connections/platform/${platform}`);
    return response.data.data;
  },

  // Re-sync username/avatar from the platform
  sync: async (id) => {
    const response = await api.put(`/api/v1/platforms/connections/${id}/sync`);
    invalidateConnections();
    return response.data.data;
  },

  // Trigger an immediate metrics pull for a connection
  pullMetrics: async (id) => {
    const response = await api.post(`/api/v1/platforms/connections/${id}/pull-metrics`);
    return response.data.data;
  },

  // Get aggregated stats across all connected platforms
  getStats: async () => {
    const response = await api.get('/api/v1/platforms/stats');
    return response.data.data;
  },

  // Get profile info (avatar, display name, followers) for all connected platforms (cached)
  getProfiles: async (force = false) => {
    if (!force && _isFresh(_cache.profilesAt)) return _cache.profiles;
    const response = await api.get('/api/v1/platforms/connections/profiles');
    _cache.profiles = response.data.data;
    _cache.profilesAt = Date.now();
    return _cache.profiles;
  },

  // ── Batch Discovery / Connect (Instagram & Facebook multi-account) ────
  discoverFacebookPages: async () => {
    const response = await api.get('/api/v1/platforms/facebook/pages');
    return response.data.data;
  },

  connectFacebookPages: async (pageIds) => {
    const response = await api.post('/api/v1/platforms/facebook/pages/connect', { pageIds });
    invalidateConnections();
    return response.data.data;
  },

  discoverInstagramAccounts: async () => {
    const response = await api.get('/api/v1/platforms/instagram/accounts');
    return response.data.data;
  },

  connectInstagramAccounts: async (instagramAccountIds) => {
    const response = await api.post('/api/v1/platforms/instagram/accounts/connect', { instagramAccountIds });
    invalidateConnections();
    return response.data.data;
  },

  // ── Admin Platform Management ──────────────────────────────────────────

  // Get ALL platform definitions (including inactive) — admin only
  adminGetAllPlatforms: async () => {
    const response = await api.get('/api/admin/platforms');
    return response.data;
  },

  // Create a new platform definition — admin only
  adminCreatePlatform: async (dto) => {
    const response = await api.post('/api/admin/platforms', dto);
    return response.data;
  },

  // Update a platform definition — admin only
  adminUpdatePlatform: async (platformKey, dto) => {
    const response = await api.put(`/api/admin/platforms/${platformKey}`, dto);
    return response.data;
  },

  // Toggle platform active/inactive — admin only
  adminTogglePlatform: async (platformKey, active) => {
    const response = await api.patch(`/api/admin/platforms/${platformKey}/toggle?active=${active}`);
    return response.data;
  },

  // Delete a platform definition — admin only
  adminDeletePlatform: async (platformKey) => {
    await api.delete(`/api/admin/platforms/${platformKey}`);
  },

  // Per-user platform connection diagnostics (status, token expiry, scopes, live check) — admin only
  adminGetDiagnostics: async (userId) => {
    const response = await api.get(`/api/admin/platform-diagnostics/${userId}`);
    return response.data;
  },
};

export default platformService;
